import { Spacing } from '@persona-id/design-tokens';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { tForGovernmentId } from 'lib/governmentId';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import ScannerOverlay from 'verify/components/ScannerOverlay';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { CaptureMethod, ClientExtraction } from 'verify/models/verification';
import BarcodeScanner from 'verify/scanners/BarcodeScanner';
import GovernmentIdMicroblinkScanner from 'verify/scanners/GovernmentIdMicroblinkScanner';
import GovernmentIdScanner from 'verify/scanners/GovernmentIdScanner';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[4]};
`;

const Title = ({
  countryCode,
  governmentIdClass,
  governmentIdSide,
}: {
  countryCode?: CountryCode;
  governmentIdClass: GovernmentIdClass;
  governmentIdSide: GovernmentIdSide;
}) => {
  const [t] = useTranslation();

  const title = tForGovernmentId(
    t,
    ['government-id.capture.title', 'government-id.title'],
    countryCode,
    governmentIdClass,
    governmentIdSide,
  );

  return (
    <ThemedTitle>
      <Markdown>{title}</Markdown>
    </ThemedTitle>
  );
};

const Prompt = ({
  governmentIdClass,
  governmentIdSide,
}: {
  governmentIdClass: GovernmentIdClass;
  governmentIdSide: GovernmentIdSide;
}) => {
  const [t] = useTranslation();
  const prompt = tForGovernmentId(
    t,
    ['government-id.capture.prompt'],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    null,
    governmentIdClass,
    governmentIdSide,
  );

  return (
    <ThemedParagraph>
      <Markdown>{prompt}</Markdown>
    </ThemedParagraph>
  );
};

interface InquiryGovernmentIdCaptureViewProps {
  localizations: StepGovernmentIdConfig['localizations']['capture-page'];
  featureFlags: FeatureFlagsMap;
  governmentIdClass: GovernmentIdClass;
  handleCapture: (
    imageDataUris: string[],
    captureMethod: CaptureMethod,
    cameraInspectionData: CameraInspectionData,
    clientExtractions?: ClientExtraction[],
    clientExtractionSource?: string,
  ) => Promise<void>;
  handleError: (error: Error | string, state: 'load' | 'capture') => void;
  requiresSide: GovernmentIdSide;
  selectedCountryCode: StepGovernmentIdConfig['selected-country-code'] | undefined;
  shouldUseMicroblink: boolean;
  videoConstraints: MediaTrackConstraints;
  webCameraManualCaptureDelayMs: number | null;
  barcodeCameraManualCaptureDelayMs: number | null;
  zoomFactor: number;
}

export default function InquiryGovernmentIdCaptureView({
  localizations,
  featureFlags,
  governmentIdClass,
  handleCapture,
  handleError,
  requiresSide,
  selectedCountryCode,
  shouldUseMicroblink,
  videoConstraints,
  webCameraManualCaptureDelayMs,
  barcodeCameraManualCaptureDelayMs,
  zoomFactor,
}: InquiryGovernmentIdCaptureViewProps) {
  const overlay = (
    <ScannerOverlay
      governmentIdClass={governmentIdClass}
      requiresSide={requiresSide}
      mirrored={videoConstraints.facingMode === 'user'}
      hideBackOverlay={
        featureFlags?.['inquiry:remove-back-overlay-for-non-us-govid'] &&
        selectedCountryCode !== 'US'
      }
    />
  );

  let scannerComponent;

  if (requiresSide === GovernmentIdSide.BarcodePdf417) {
    if (shouldUseMicroblink) {
      scannerComponent = (
        <GovernmentIdMicroblinkScanner
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          delayUntilManualCaptureMs={barcodeCameraManualCaptureDelayMs}
          governmentIdClass={governmentIdClass}
          handleCapture={handleCapture}
          handleError={handleError}
          labelTakePhoto={localizations['btn-take-photo']}
          overlay={overlay}
          requiresSide={requiresSide}
          videoConstraints={videoConstraints}
          zoomFactor={zoomFactor}
        />
      );
    } else {
      scannerComponent = (
        <BarcodeScanner
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          delayUntilManualCaptureMs={barcodeCameraManualCaptureDelayMs}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCapture={handleCapture}
          handleError={handleError}
          labelTakePhoto={localizations['btn-take-photo']}
          videoConstraints={videoConstraints}
          requiresSide={requiresSide}
          overlay={overlay}
          zoomFactor={zoomFactor}
        />
      );
    }
  } else {
    scannerComponent = shouldUseMicroblink ? (
      <GovernmentIdMicroblinkScanner
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        delayUntilManualCaptureMs={webCameraManualCaptureDelayMs}
        governmentIdClass={governmentIdClass}
        handleCapture={handleCapture}
        handleError={handleError}
        labelTakePhoto={localizations['btn-take-photo']}
        overlay={overlay}
        requiresSide={requiresSide}
        videoConstraints={videoConstraints}
        zoomFactor={zoomFactor}
      />
    ) : (
      <GovernmentIdScanner
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        delayUntilManualCaptureMs={webCameraManualCaptureDelayMs}
        handleCapture={handleCapture}
        handleError={handleError}
        isPassport={governmentIdClass === 'pp' || governmentIdClass === 'ipp'}
        labelTakePhoto={localizations['btn-take-photo']}
        overlay={overlay}
        videoConstraints={videoConstraints}
        zoomFactor={zoomFactor}
      />
    );
  }

  return (
    <div>
      <Header>
        <Title
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          countryCode={selectedCountryCode}
          governmentIdClass={governmentIdClass}
          governmentIdSide={requiresSide}
        />
        <Prompt governmentIdClass={governmentIdClass} governmentIdSide={requiresSide} />
      </Header>
      {scannerComponent}
    </div>
  );
}
